import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    Plus,
    CreditCard,
    TrendingUp,
    BarChart3,
    History,
    Wallet,
    Activity,
    RefreshCw,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { formatCurrency } from '@/lib/format';
import { Skeleton } from '@/components/ui/skeleton';
import TradingAccountModal from './TradingAccountModal';
import TradingAccountDetailsModal from './TradingAccountDetailsModal';
import CreateTradeModal from './CreateTradeModal';
import {
    TradingAccount,
    useCustomerTradingAccounts,
} from '@/api/trading-accounts/use-trading-accounts';
import { useRecalculateTradingAccountBalance } from '@/api/trading-accounts/use-crm-trading-accounts';
import { useSnackbar } from 'notistack';
import TradesList from '@/components/trading/TradesList';

interface CustomerTradingTabProps {
    customerId: string;
}

const CustomerTradingTab: React.FC<CustomerTradingTabProps> = ({ customerId }) => {
    const [activeTab, setActiveTab] = useState('pending');
    const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);
    const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] = useState(false);
    const [isCreateTradeModalOpen, setIsCreateTradeModalOpen] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState<TradingAccount | null>(null);

    const { hasPermission } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();
    const recalculateBalanceMutation = useRecalculateTradingAccountBalance();

    // Fetch trading accounts data for this customer
    const {
        data: accountsData,
        isLoading: isLoadingAccounts,
        refetch: refetchAccounts,
    } = useCustomerTradingAccounts(customerId);
    const accounts = accountsData?.accounts || [];

    const handleCreateAccount = () => {
        setIsCreateAccountModalOpen(true);
    };

    const handleAccountCreated = () => {
        refetchAccounts();
        setIsCreateAccountModalOpen(false);
        enqueueSnackbar('Trading account created successfully', { variant: 'success' });
    };

    const handleAccountClick = (account: TradingAccount) => {
        setSelectedAccount(account);
        setIsAccountDetailsModalOpen(true);
    };

    const handleCreateTrade = () => {
        setIsCreateTradeModalOpen(true);
    };

    const handleRecalculateBalance = async (account: TradingAccount, e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            await recalculateBalanceMutation.mutateAsync(account.id);
            enqueueSnackbar(
                `Balance recalculated for account #${account.account_number ?? account.id}`,
                {
                    variant: 'success',
                }
            );
            refetchAccounts();
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || 'Failed to recalculate account balance',
                { variant: 'error' }
            );
        }
    };

    const handleTradeCreated = () => {
        setIsCreateTradeModalOpen(false);
        refetchAccounts();
    };

    // Check permissions and account limit
    const canCreateAccount = hasPermission(
        ModulePermission.TRADING_ACCOUNTS,
        PermissionAction.CREATE
    );
    const canRecalculateAccountBalance = hasPermission(
        ModulePermission.TRADING_ACCOUNTS,
        PermissionAction.EDIT
    );
    const MAX_ACCOUNTS_PER_CUSTOMER = 2;
    const hasReachedMaxAccounts = accounts.length >= MAX_ACCOUNTS_PER_CUSTOMER;
    const isCreateAccountDisabled = !canCreateAccount || hasReachedMaxAccounts;

    // Determine tooltip message
    const getCreateAccountTooltip = () => {
        if (!canCreateAccount) {
            return 'You do not have permission to create trading accounts';
        }
        if (hasReachedMaxAccounts) {
            return `Maximum of ${MAX_ACCOUNTS_PER_CUSTOMER} accounts per customer reached`;
        }
        return 'Create a new trading account';
    };

    return (
        <TooltipProvider>
            <div className="space-y-6">
                {/* Trading Accounts Section */}
                <Card>
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle className="flex items-center gap-2">
                                <Wallet className="h-5 w-5" />
                                Trading Accounts
                            </CardTitle>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        onClick={handleCreateAccount}
                                        size="sm"
                                        disabled={isCreateAccountDisabled}
                                    >
                                        <Plus className="h-4 w-4" />
                                        Create Account
                                    </Button>
                                </TooltipTrigger>
                                {isCreateAccountDisabled && (
                                    <TooltipContent>
                                        <p>{getCreateAccountTooltip()}</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {isLoadingAccounts ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <Card key={i}>
                                        <CardContent className="p-4 space-y-3">
                                            <Skeleton className="h-5 w-32" />
                                            <Skeleton className="h-4 w-20" />
                                            <Skeleton className="h-4 w-full" />
                                            <Skeleton className="h-4 w-full" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : accounts.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No trading accounts found for this customer.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {accounts.map(account => (
                                    <Card
                                        key={account.id}
                                        className="cursor-pointer hover:shadow-md transition-shadow"
                                        onClick={() => handleAccountClick(account)}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h3 className="font-semibold">
                                                        Account #{account.account_number}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        {account.base_currency}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {canRecalculateAccountBalance && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={e =>
                                                                handleRecalculateBalance(account, e)
                                                            }
                                                            disabled={
                                                                recalculateBalanceMutation.isPending
                                                            }
                                                            title="Recalculate account balance"
                                                        >
                                                            <RefreshCw
                                                                className={`h-4 w-4 ${recalculateBalanceMutation.isPending ? 'animate-spin' : ''}`}
                                                            />
                                                        </Button>
                                                    )}
                                                    <Badge variant="outline">
                                                        {account.leverage}x
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="flex justify-between text-sm">
                                                    <span>Balance:</span>
                                                    <span className="font-medium">
                                                        {formatCurrency(
                                                            account.balance,
                                                            account.base_currency
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Bonuses:</span>
                                                    <span className="font-medium">
                                                        {formatCurrency(
                                                            account.total_bonuses ?? 0,
                                                            account.base_currency
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Equity:</span>
                                                    <span className="font-medium">
                                                        {formatCurrency(
                                                            account.equity,
                                                            account.base_currency
                                                        )}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Margin:</span>
                                                    <span className="font-medium">
                                                        {formatCurrency(
                                                            account.used_margin,
                                                            account.base_currency
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Trading Activity Section */}
                <Card>
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle className="flex items-center gap-2">
                                <Activity className="h-5 w-5" />
                                Trading Activity
                            </CardTitle>
                            {hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE) && (
                                <Button onClick={handleCreateTrade} size="sm">
                                    <Plus className="h-4 w-4" />
                                    Create Trade
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList>
                                <TabsTrigger value="pending">Pending Orders</TabsTrigger>
                                <TabsTrigger value="open">Open Positions</TabsTrigger>
                                <TabsTrigger value="closed">Trade History</TabsTrigger>
                            </TabsList>

                            <TabsContent value="pending">
                                <TradesList
                                    status="pending"
                                    title="Pending Orders"
                                    description="Orders waiting for execution"
                                    customerId={customerId}
                                    hideCustomerColumn={true}
                                    showExport={false}
                                    showRefresh={false}
                                />
                            </TabsContent>

                            <TabsContent value="open">
                                <TradesList
                                    status="open"
                                    title="Open Positions"
                                    description="Active trading positions"
                                    customerId={customerId}
                                    hideCustomerColumn={true}
                                    showExport={false}
                                    showRefresh={false}
                                />
                            </TabsContent>

                            <TabsContent value="closed">
                                <TradesList
                                    status="closed"
                                    title="Trade History"
                                    description="Completed trades"
                                    customerId={customerId}
                                    hideCustomerColumn={true}
                                    showExport={false}
                                    showRefresh={false}
                                />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Modals */}
                <TradingAccountModal
                    open={isCreateAccountModalOpen}
                    onClose={() => setIsCreateAccountModalOpen(false)}
                    onSuccess={handleAccountCreated}
                    customerId={customerId}
                />

                {selectedAccount && (
                    <TradingAccountDetailsModal
                        open={isAccountDetailsModalOpen}
                        onClose={() => {
                            setIsAccountDetailsModalOpen(false);
                            setSelectedAccount(null);
                        }}
                        account={selectedAccount}
                        onAccountUpdated={refetchAccounts}
                    />
                )}

                <CreateTradeModal
                    isOpen={isCreateTradeModalOpen}
                    onClose={() => setIsCreateTradeModalOpen(false)}
                    onSuccess={handleTradeCreated}
                    customerId={customerId}
                />
            </div>
        </TooltipProvider>
    );
};

export default CustomerTradingTab;
